import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { MarketplaceItem, MarketplaceCoupon, marketplaceCategories, marketplaceStatuses, couponDiscountTypes } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Trash2, Pencil, Package, Ticket, Eye, MousePointerClick, Check, X } from "lucide-react";
import { ko } from "date-fns/locale";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

const regions = [
  { value: "ALL", label: "전체" },
  { value: "NSW", label: "뉴사우스웨일스" },
  { value: "VIC", label: "빅토리아" },
  { value: "QLD", label: "퀸즐랜드" },
  { value: "SA", label: "남호주" },
  { value: "WA", label: "서호주" },
  { value: "TAS", label: "태즈매니아" },
  { value: "NT", label: "노던 테리토리" },
  { value: "ACT", label: "수도 준주" },
];

export function AdminMarketplace() {
  const { toast } = useToast();
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [couponDialogOpen, setCouponDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<MarketplaceItem | null>(null);
  const [editingCoupon, setEditingCoupon] = useState<MarketplaceCoupon | null>(null);

  // Fetch marketplace items
  const { data: items = [], isLoading: itemsLoading } = useQuery<MarketplaceItem[]>({
    queryKey: ["/api/marketplace/items"],
  });

  // Fetch marketplace coupons
  const { data: coupons = [], isLoading: couponsLoading } = useQuery<MarketplaceCoupon[]>({
    queryKey: ["/api/marketplace/coupons"],
  });

  // Delete item mutation
  const deleteItemMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/marketplace/items/${id}`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/marketplace/items"] });
      toast({
        title: "삭제 완료",
        description: "상품이 삭제되었습니다.",
      });
    },
  });

  // Delete coupon mutation
  const deleteCouponMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/marketplace/coupons/${id}`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/marketplace/coupons"] });
      toast({
        title: "삭제 완료",
        description: "쿠폰이 삭제되었습니다.",
      });
    },
  });

  // Approve item mutation
  const approveItemMutation = useMutation({
    mutationFn: (id: string) => apiRequest("PATCH", `/api/marketplace/items/${id}`, { status: "active" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/marketplace/items"] });
      toast({
        title: "승인 완료",
        description: "상품이 승인되었습니다.",
      });
    },
  });

  // Reject item mutation
  const rejectItemMutation = useMutation({
    mutationFn: (id: string) => apiRequest("PATCH", `/api/marketplace/items/${id}`, { status: "rejected" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/marketplace/items"] });
      toast({
        title: "거부 완료",
        description: "상품이 거부되었습니다.",
      });
    },
  });

  const handleEditItem = (item: MarketplaceItem) => {
    setEditingItem(item);
    setItemDialogOpen(true);
  };

  const handleEditCoupon = (coupon: MarketplaceCoupon) => {
    setEditingCoupon(coupon);
    setCouponDialogOpen(true);
  };

  const getCategoryLabel = (categoryId: string) => {
    return marketplaceCategories.find(c => c.id === categoryId)?.label || categoryId;
  };

  const getStatusBadge = (status: string) => {
    const statusInfo = marketplaceStatuses.find(s => s.id === status);
    const colorMap: Record<string, string> = {
      green: "bg-green-500/10 text-green-700 dark:text-green-400",
      yellow: "bg-yellow-500/10 text-yellow-700 dark:text-yellow-400",
      red: "bg-red-500/10 text-red-700 dark:text-red-400",
      gray: "bg-gray-500/10 text-gray-700 dark:text-gray-400",
    };
    return (
      <Badge className={colorMap[statusInfo?.color || "gray"]}>
        {statusInfo?.label || status}
      </Badge>
    );
  };

  return (
    <div className="space-y-4">
      <Tabs defaultValue="items" className="space-y-4">
        <TabsList>
          <TabsTrigger value="items" data-testid="tab-marketplace-items">
            <Package className="h-4 w-4 mr-2" />
            상품 관리
          </TabsTrigger>
          <TabsTrigger value="coupons" data-testid="tab-marketplace-coupons">
            <Ticket className="h-4 w-4 mr-2" />
            쿠폰 관리
          </TabsTrigger>
        </TabsList>

        {/* Items Management */}
        <TabsContent value="items" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-2">
              <CardTitle>상품 목록</CardTitle>
              <Dialog open={itemDialogOpen} onOpenChange={setItemDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    onClick={() => setEditingItem(null)}
                    data-testid="button-add-item"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    상품 등록
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>
                      {editingItem ? "상품 수정" : "상품 등록"}
                    </DialogTitle>
                    <DialogDescription>
                      핫샵에 등록할 상품 정보를 입력하세요.
                    </DialogDescription>
                  </DialogHeader>
                  <ItemForm
                    item={editingItem}
                    onClose={() => {
                      setItemDialogOpen(false);
                      setEditingItem(null);
                    }}
                  />
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {itemsLoading ? (
                <div className="text-center py-8 text-muted-foreground">로딩 중...</div>
              ) : items.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  등록된 상품이 없습니다.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>업체명</TableHead>
                      <TableHead>상품명</TableHead>
                      <TableHead>카테고리</TableHead>
                      <TableHead>지역</TableHead>
                      <TableHead>가격</TableHead>
                      <TableHead>상태</TableHead>
                      <TableHead>통계</TableHead>
                      <TableHead className="text-right">관리</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item) => (
                      <TableRow key={item.id} data-testid={`row-item-${item.id}`}>
                        <TableCell className="font-medium">{item.businessName}</TableCell>
                        <TableCell>
                          <div>
                            <div>{item.titleKo || item.title}</div>
                            {item.featured && (
                              <Badge variant="secondary" className="mt-1">추천</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{getCategoryLabel(item.category)}</TableCell>
                        <TableCell>{regions.find(r => r.value === item.region)?.label}</TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="font-bold">${Number(item.salePrice).toFixed(2)}</div>
                            {item.originalPrice && (
                              <div className="text-xs text-muted-foreground line-through">
                                ${Number(item.originalPrice).toFixed(2)}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(item.status || "active")}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3 text-sm text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Eye className="h-3 w-3" />
                              {item.views || 0}
                            </div>
                            <div className="flex items-center gap-1">
                              <MousePointerClick className="h-3 w-3" />
                              {item.clicks || 0}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            {item.status === "pending" ? (
                              <>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => {
                                    if (confirm("이 상품을 승인하시겠습니까?")) {
                                      approveItemMutation.mutate(item.id);
                                    }
                                  }}
                                  data-testid={`button-approve-item-${item.id}`}
                                  className="text-green-600 hover:text-green-700"
                                >
                                  <Check className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => {
                                    if (confirm("이 상품을 거부하시겠습니까?")) {
                                      rejectItemMutation.mutate(item.id);
                                    }
                                  }}
                                  data-testid={`button-reject-item-${item.id}`}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleEditItem(item)}
                                  data-testid={`button-edit-item-${item.id}`}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => {
                                    if (confirm("정말 삭제하시겠습니까?")) {
                                      deleteItemMutation.mutate(item.id);
                                    }
                                  }}
                                  data-testid={`button-delete-item-${item.id}`}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Coupons Management */}
        <TabsContent value="coupons" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-2">
              <CardTitle>쿠폰 목록</CardTitle>
              <Dialog open={couponDialogOpen} onOpenChange={setCouponDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    onClick={() => setEditingCoupon(null)}
                    data-testid="button-add-coupon"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    쿠폰 등록
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>
                      {editingCoupon ? "쿠폰 수정" : "쿠폰 등록"}
                    </DialogTitle>
                    <DialogDescription>
                      핫샵에 등록할 쿠폰 정보를 입력하세요.
                    </DialogDescription>
                  </DialogHeader>
                  <CouponForm
                    coupon={editingCoupon}
                    onClose={() => {
                      setCouponDialogOpen(false);
                      setEditingCoupon(null);
                    }}
                  />
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {couponsLoading ? (
                <div className="text-center py-8 text-muted-foreground">로딩 중...</div>
              ) : coupons.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  등록된 쿠폰이 없습니다.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>업체명</TableHead>
                      <TableHead>쿠폰명</TableHead>
                      <TableHead>코드</TableHead>
                      <TableHead>할인</TableHead>
                      <TableHead>회원등급</TableHead>
                      <TableHead>기간</TableHead>
                      <TableHead>사용</TableHead>
                      <TableHead>상태</TableHead>
                      <TableHead className="text-right">관리</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {coupons.map((coupon) => (
                      <TableRow key={coupon.id} data-testid={`row-coupon-${coupon.id}`}>
                        <TableCell className="font-medium">{coupon.businessName}</TableCell>
                        <TableCell>
                          <div>
                            <div>{coupon.titleKo || coupon.title}</div>
                            {coupon.featured && (
                              <Badge variant="secondary" className="mt-1">추천</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <code className="bg-muted px-2 py-1 rounded text-sm">
                            {coupon.code}
                          </code>
                        </TableCell>
                        <TableCell>
                          {coupon.discountType === "percentage"
                            ? `${Number(coupon.discountValue)}%`
                            : `$${Number(coupon.discountValue)}`
                          }
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {coupon.membershipRequired === "free" && "무료"}
                            {coupon.membershipRequired === "premium" && "프리미엄"}
                            {coupon.membershipRequired === "platinum" && "플래티넘"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          <div>{coupon.startDate}</div>
                          <div className="text-muted-foreground">~ {coupon.endDate}</div>
                        </TableCell>
                        <TableCell>
                          {coupon.usedCount || 0}
                          {coupon.usageLimit && ` / ${coupon.usageLimit}`}
                        </TableCell>
                        <TableCell>{getStatusBadge(coupon.status || "active")}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEditCoupon(coupon)}
                              data-testid={`button-edit-coupon-${coupon.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                if (confirm("정말 삭제하시겠습니까?")) {
                                  deleteCouponMutation.mutate(coupon.id);
                                }
                              }}
                              data-testid={`button-delete-coupon-${coupon.id}`}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Item Form Component
function ItemForm({ item, onClose }: { item: MarketplaceItem | null; onClose: () => void }) {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    businessName: item?.businessName || "",
    category: item?.category || "food",
    region: item?.region || "ALL",
    title: item?.title || "",
    titleKo: item?.titleKo || "",
    description: item?.description || "",
    descriptionKo: item?.descriptionKo || "",
    originalPrice: item?.originalPrice ? Number(item.originalPrice) : "",
    salePrice: item?.salePrice ? Number(item.salePrice) : "",
    discountRate: item?.discountRate || "",
    imageUrl: item?.imageUrl || "",
    contactPhone: item?.contactPhone || "",
    contactEmail: item?.contactEmail || "",
    website: item?.website || "",
    address: item?.address || "",
    city: item?.city || "",
    state: item?.state || "",
    postcode: item?.postcode || "",
    isPremiumOnly: item?.isPremiumOnly || false,
    isPlatinumOnly: item?.isPlatinumOnly || false,
    status: item?.status || "active",
    featured: item?.featured || false,
    priority: item?.priority || 5,
    startDate: item?.startDate || "",
    endDate: item?.endDate || "",
  });

  const saveMutation = useMutation({
    mutationFn: (data: any) => {
      if (item) {
        return apiRequest("PATCH", `/api/marketplace/items/${item.id}`, data);
      } else {
        return apiRequest("POST", "/api/marketplace/items", data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/marketplace/items"] });
      toast({
        title: item ? "수정 완료" : "등록 완료",
        description: item ? "상품이 수정되었습니다." : "상품이 등록되었습니다.",
      });
      onClose();
    },
    onError: (error: any) => {
      toast({
        title: "오류",
        description: error.message || "처리에 실패했습니다.",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveMutation.mutate(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="businessName">업체명 *</Label>
          <Input
            id="businessName"
            value={formData.businessName}
            onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
            required
            data-testid="input-business-name"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="category">카테고리 *</Label>
          <Select
            value={formData.category}
            onValueChange={(value) => setFormData({ ...formData, category: value })}
          >
            <SelectTrigger data-testid="select-category">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {marketplaceCategories.map((cat) => (
                <SelectItem key={cat.id} value={cat.id}>
                  {cat.icon} {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="title">상품명 (English) *</Label>
          <Input
            id="title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
            data-testid="input-title"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="titleKo">상품명 (한글)</Label>
          <Input
            id="titleKo"
            value={formData.titleKo}
            onChange={(e) => setFormData({ ...formData, titleKo: e.target.value })}
            data-testid="input-title-ko"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">설명 (English) *</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={3}
          required
          data-testid="textarea-description"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="descriptionKo">설명 (한글)</Label>
        <Textarea
          id="descriptionKo"
          value={formData.descriptionKo}
          onChange={(e) => setFormData({ ...formData, descriptionKo: e.target.value })}
          rows={3}
          data-testid="textarea-description-ko"
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="originalPrice">정가 ($)</Label>
          <Input
            id="originalPrice"
            type="number"
            step="0.01"
            value={formData.originalPrice}
            onChange={(e) => setFormData({ ...formData, originalPrice: e.target.value })}
            data-testid="input-original-price"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="salePrice">판매가 ($) *</Label>
          <Input
            id="salePrice"
            type="number"
            step="0.01"
            value={formData.salePrice}
            onChange={(e) => setFormData({ ...formData, salePrice: e.target.value })}
            required
            data-testid="input-sale-price"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="discountRate">할인율 (%)</Label>
          <Input
            id="discountRate"
            type="number"
            value={formData.discountRate}
            onChange={(e) => setFormData({ ...formData, discountRate: e.target.value })}
            data-testid="input-discount-rate"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="imageUrl">이미지 URL *</Label>
        <Input
          id="imageUrl"
          type="url"
          value={formData.imageUrl}
          onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
          placeholder="https://example.com/image.jpg"
          required
          data-testid="input-image-url"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="contactPhone">연락처 *</Label>
          <Input
            id="contactPhone"
            type="tel"
            value={formData.contactPhone}
            onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
            required
            data-testid="input-contact-phone"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="contactEmail">이메일</Label>
          <Input
            id="contactEmail"
            type="email"
            value={formData.contactEmail}
            onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
            data-testid="input-contact-email"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="website">웹사이트</Label>
        <Input
          id="website"
          type="url"
          value={formData.website}
          onChange={(e) => setFormData({ ...formData, website: e.target.value })}
          placeholder="https://example.com"
          data-testid="input-website"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="region">지역</Label>
          <Select
            value={formData.region}
            onValueChange={(value) => setFormData({ ...formData, region: value })}
          >
            <SelectTrigger data-testid="select-region">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {regions.map((r) => (
                <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="status">상태</Label>
          <Select
            value={formData.status}
            onValueChange={(value) => setFormData({ ...formData, status: value })}
          >
            <SelectTrigger data-testid="select-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {marketplaceStatuses.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="flex items-center space-x-2">
          <Switch
            id="featured"
            checked={formData.featured}
            onCheckedChange={(checked) => setFormData({ ...formData, featured: checked })}
            data-testid="switch-featured"
          />
          <Label htmlFor="featured">추천 상품</Label>
        </div>
        <div className="flex items-center space-x-2">
          <Switch
            id="isPremiumOnly"
            checked={formData.isPremiumOnly}
            onCheckedChange={(checked) => setFormData({ ...formData, isPremiumOnly: checked })}
            data-testid="switch-premium-only"
          />
          <Label htmlFor="isPremiumOnly">프리미엄 전용</Label>
        </div>
        <div className="flex items-center space-x-2">
          <Switch
            id="isPlatinumOnly"
            checked={formData.isPlatinumOnly}
            onCheckedChange={(checked) => setFormData({ ...formData, isPlatinumOnly: checked })}
            data-testid="switch-platinum-only"
          />
          <Label htmlFor="isPlatinumOnly">플래티넘 전용</Label>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onClose} data-testid="button-cancel">
          취소
        </Button>
        <Button type="submit" disabled={saveMutation.isPending} data-testid="button-submit">
          {saveMutation.isPending ? "저장 중..." : (item ? "수정" : "등록")}
        </Button>
      </div>
    </form>
  );
}

// Coupon Form Component (similar structure)
function CouponForm({ coupon, onClose }: { coupon: MarketplaceCoupon | null; onClose: () => void }) {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    businessName: coupon?.businessName || "",
    title: coupon?.title || "",
    titleKo: coupon?.titleKo || "",
    code: coupon?.code || "",
    description: coupon?.description || "",
    descriptionKo: coupon?.descriptionKo || "",
    discountType: coupon?.discountType || "percentage",
    discountValue: coupon?.discountValue ? Number(coupon.discountValue) : "",
    minimumPurchase: coupon?.minimumPurchase ? Number(coupon.minimumPurchase) : "",
    maximumDiscount: coupon?.maximumDiscount ? Number(coupon.maximumDiscount) : "",
    membershipRequired: coupon?.membershipRequired || "free",
    startDate: coupon?.startDate || new Date().toISOString().split('T')[0],
    endDate: coupon?.endDate || "",
    usageLimit: coupon?.usageLimit || "",
    status: coupon?.status || "active",
    featured: coupon?.featured || false,
  });

  const saveMutation = useMutation({
    mutationFn: (data: any) => {
      if (coupon) {
        return apiRequest("PATCH", `/api/marketplace/coupons/${coupon.id}`, data);
      } else {
        return apiRequest("POST", "/api/marketplace/coupons", data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/marketplace/coupons"] });
      toast({
        title: coupon ? "수정 완료" : "등록 완료",
        description: coupon ? "쿠폰이 수정되었습니다." : "쿠폰이 등록되었습니다.",
      });
      onClose();
    },
    onError: (error: any) => {
      toast({
        title: "오류",
        description: error.message || "처리에 실패했습니다.",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveMutation.mutate(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="businessName">업체명 *</Label>
        <Input
          id="businessName"
          value={formData.businessName}
          onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
          required
          data-testid="input-business-name"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="title">쿠폰명 (English) *</Label>
          <Input
            id="title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
            data-testid="input-title"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="titleKo">쿠폰명 (한글)</Label>
          <Input
            id="titleKo"
            value={formData.titleKo}
            onChange={(e) => setFormData({ ...formData, titleKo: e.target.value })}
            data-testid="input-title-ko"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="code">쿠폰 코드 *</Label>
        <Input
          id="code"
          value={formData.code}
          onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
          placeholder="예: WELCOME20"
          required
          data-testid="input-code"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="discountType">할인 타입 *</Label>
          <Select
            value={formData.discountType}
            onValueChange={(value) => setFormData({ ...formData, discountType: value })}
          >
            <SelectTrigger data-testid="select-discount-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {couponDiscountTypes.map((type) => (
                <SelectItem key={type.id} value={type.id}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="discountValue">할인 값 *</Label>
          <Input
            id="discountValue"
            type="number"
            step="0.01"
            value={formData.discountValue}
            onChange={(e) => setFormData({ ...formData, discountValue: e.target.value })}
            placeholder={formData.discountType === "percentage" ? "20" : "10.00"}
            required
            data-testid="input-discount-value"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="startDate">시작일 *</Label>
          <Input
            id="startDate"
            type="date"
            value={formData.startDate}
            onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
            required
            data-testid="input-start-date"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="endDate">종료일 *</Label>
          <Input
            id="endDate"
            type="date"
            value={formData.endDate}
            onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
            required
            data-testid="input-end-date"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="membershipRequired">필요 회원등급</Label>
          <Select
            value={formData.membershipRequired}
            onValueChange={(value) => setFormData({ ...formData, membershipRequired: value })}
          >
            <SelectTrigger data-testid="select-membership">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="free">무료</SelectItem>
              <SelectItem value="premium">프리미엄</SelectItem>
              <SelectItem value="platinum">플래티넘</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="usageLimit">사용 횟수 제한</Label>
          <Input
            id="usageLimit"
            type="number"
            value={formData.usageLimit}
            onChange={(e) => setFormData({ ...formData, usageLimit: e.target.value })}
            placeholder="무제한"
            data-testid="input-usage-limit"
          />
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="flex items-center space-x-2">
          <Switch
            id="featured"
            checked={formData.featured}
            onCheckedChange={(checked) => setFormData({ ...formData, featured: checked })}
            data-testid="switch-featured"
          />
          <Label htmlFor="featured">추천 쿠폰</Label>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onClose} data-testid="button-cancel">
          취소
        </Button>
        <Button type="submit" disabled={saveMutation.isPending} data-testid="button-submit">
          {saveMutation.isPending ? "저장 중..." : (coupon ? "수정" : "등록")}
        </Button>
      </div>
    </form>
  );
}
